import { useState, useEffect, useCallback } from 'react';
import { useData } from '../contexts/DataContext';
import { ConversationApi } from '../services/ApiService';
import { AiConversationsApi, AiConversationsStream } from '../services/ai/AiConversationsApi';

const _convoCache = new Map();           // key -> { data, ts }
const _convoInflight = new Map();        // key -> Promise
const CONVO_TTL_MS = 10_000;

/** Invalidate the conversation list cache for a specific user (or all users). */
export function invalidateConversationCache(userId) {
  if (userId) {
    _convoCache.delete(`user-convos_${userId}`);
  } else {
    _convoCache.clear();
  }
}

export const useAiChat = (selectedEndeavor) => {
  const { isAiServiceAvailable } = useData();
  const [isServiceReady, setIsServiceReady] = useState(false);

  // ✅ FIXED: Check service availability using cached method
  useEffect(() => {
    const checkService = async () => {
      try {
        const isAvailable = await isAiServiceAvailable(false);
        setIsServiceReady(isAvailable);
      } catch (error) {
        console.error('useAiChat: Error checking service:', error);
        setIsServiceReady(false);
      }
    };

    checkService();
  }, [isAiServiceAvailable]);

  // ===================================
  // Data Service Conversation Management (Hibernate Service)
  // ===================================

  // REMOVED: createNewConversation - Conversations are now created by the backend
  // when the first message is sent. The Python AI service handles conversation creation
  // and returns the conversation ID in the response metadata.

  const loadExistingConversations = useCallback(async (userId) => {
    try {
      if (!userId) return [];
      const key = `user-convos_${userId}`;
      const now = Date.now();

      // cached?
      const cached = _convoCache.get(key);
      if (cached && now - cached.ts < CONVO_TTL_MS) {
        return cached.data;
      }
      // inflight?
      const inflight = _convoInflight.get(key);
      if (inflight) return inflight;

      // start request
      const p = (async () => {
        const response = await ConversationApi.getUserConversations(userId, { page: 0, size: 50 });
        if (!response?.ok) return [];
        const data = await response.json();
        const arr = data.conversations || data.content || data.data || data || [];
        const mapped = Array.isArray(arr) ? arr.map(conv => {
          // Normalize attached endeavor IDs: backend may return as plural array OR singular field
          let eIds = Array.isArray(conv.attachedEndeavorIds) && conv.attachedEndeavorIds.length
            ? conv.attachedEndeavorIds
            : [];
          if (!eIds.length) {
            const singular = conv.endeavorId || conv.endeavor_id || conv.endeavorID;
            if (singular) eIds = [singular];
          }
          return {
            id: conv.id || conv.conversationId,
            title: conv.title || '',
            createdAt: conv.createdAt || conv.created_at,
            updatedAt: conv.updatedAt || conv.updated_at || conv.lastActivityAt,
            lastActivityAt: conv.lastActivityAt,
            messageCount: conv.messageCount,
            attachedDocumentIds: Array.isArray(conv.attachedDocumentIds) ? conv.attachedDocumentIds : [],
            attachedEndeavorIds: eIds
          };
        }) : [];
        _convoCache.set(key, { data: mapped, ts: Date.now() });
        _convoInflight.delete(key);
        return mapped;
      })();

      _convoInflight.set(key, p);
      return p;
    } catch (e) {
      console.error('loadExistingConversations error:', e);
      return [];
    }
  }, []);

  // ✅ FIXED: Helper method to store conversation messages (moved before processUserMessage)
  const storeConversationMessages = useCallback(async (conversationId, userMessage, aiResponse, qualityLevel) => {
    try {
      
    } catch (error) {
      console.error('Error storing conversation messages:', error);
    }
  }, []);

  // ✅ UPDATED: Load conversation messages with pagination support
  const loadConversationMessages = useCallback(async (conversationId, options = {}) => {
    if (!conversationId) return { messages: [], pagination: null };
    
    try {
      console.log('Loading messages for conversation:', conversationId, 'with options:', options);
      
      // ✅ NEW: Use separate messages endpoint with pagination
      const response = await ConversationApi.getConversationMessages(conversationId, {
        page: options.page || 0,
        size: options.size || 10
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('Raw messages response:', data);
        
        const rawMessages = data.messages || [];
        
        // ✅ FIXED: Messages come in reverse order (newest first), so reverse them for chronological display
        const chronologicalMessages = [...rawMessages].reverse();
        
        // ✅ FIXED: Map API message format to UI message format
        const formattedMessages = chronologicalMessages.map(msg => ({
          id: `msg_${msg.id}_${Date.now()}`,
          text: msg.content || msg.text || '',
          sender: msg.role === 'user' ? 'user' : 'ai',
          timestamp: new Date(msg.createdAt || msg.timestamp || new Date()),
          scoutState: msg.metadata?.scoutState || 'default',
          mood: msg.metadata?.mood || 'neutral',
          documentReferences: msg.metadata?.documentReferences || [],
          suggestedActions: msg.metadata?.suggestedActions || [],
          modelUsed: msg.modelUsed,
          tokenCount: msg.tokenCount,
          messageOrder: msg.messageOrder,
          qualityLevel: msg.metadata?.qualityLevel || 'balanced'
        }));
        
        // ✅ NEW: Return messages with pagination info
        return {
          messages: formattedMessages,
          pagination: {
            page: data.page || 0,
            size: data.size || 10,
            totalPages: data.totalPages || 1,
            totalElements: data.totalElements || formattedMessages.length,
            hasNext: data.hasNext || false,
            hasPrevious: data.hasPrevious || false
          }
        };
      }
      return { messages: [], pagination: null };
    } catch (error) {
      console.error('Error loading conversation messages:', error);
      return { messages: [], pagination: null };
    }
  }, []);

  // ✅ NEW: Load more messages (for pagination)
  const loadMoreMessages = useCallback(async (conversationId, currentPage, size = 10) => {
    try {
      console.log('Loading more messages for conversation:', conversationId, 'page:', currentPage + 1);
      
      const result = await loadConversationMessages(conversationId, {
        page: currentPage + 1,
        size
      });
      
      return result;
    } catch (error) {
      console.error('Error loading more messages:', error);
      return { messages: [], pagination: null };
    }
  }, [loadConversationMessages]);

  const updateConversationTitle = useCallback(async (conversationId, title) => {
    try {
      const response = await ConversationApi.updateConversationTitle(conversationId, title);
      return response.ok;
    } catch (error) {
      console.error('Error updating conversation title:', error);
      return false;
    }
  }, []);

  const patchConversation = useCallback(async (conversationId, updates) => {
    try {
      const response = await ConversationApi.updateConversation(conversationId, updates);
      return response.ok;
    } catch (error) {
      console.error('Error patching conversation:', error);
      return false;
    }
  }, []);

  // Replace existing deleteConversation implementation
  const deleteConversation = useCallback(async (id) => {
    try {
      const resp = await ConversationApi.deleteConversation(id);
      if (!resp.ok) throw new Error(`Delete failed ${resp.status}`);
      return true;
    } catch (e) {
      console.warn('useAiChat: delete failed', e);
      return false;
    }
  }, []);

  // ===================================
  // AI Service Chat Processing (Python Service)
  // ===================================

  // Load a conversation’s details (attached docs/courses, title, etc.)
  const loadConversationDetails = useCallback(async (conversationId) => {
    if (!conversationId) return null;
    try {
      const resp = await ConversationApi.getConversation(conversationId);
      if (!resp?.ok) return null;
      const data = await resp.json();
      // Normalize attached endeavor IDs: backend may return as plural array OR singular field
      let eIds = Array.isArray(data.attachedEndeavorIds) && data.attachedEndeavorIds.length
        ? data.attachedEndeavorIds
        : [];
      if (!eIds.length) {
        const singular = data.endeavorId || data.endeavor_id || data.endeavorID;
        if (singular) eIds = [singular];
      }
      return {
        id: data.id || data.conversationId,
        conversationId: data.conversationId || data.id,
        title: data.title,
        createdAt: data.createdAt,
        updatedAt: data.updatedAt || data.lastActivityAt,
        attachedDocumentIds: Array.isArray(data.attachedDocumentIds) ? data.attachedDocumentIds : [],
        attachedEndeavorIds: eIds,
        messageCount: data.messageCount
      };
    } catch (e) {
      console.warn('loadConversationDetails: failed', e);
      return null;
    }
  }, []);

  // ✅ FIXED: Process user message with quality level support and proper dependencies
  const processUserMessage = useCallback(async (message, context = {}) => {
    try {
      console.log('Processing message:', message);
      console.log('Context:', context);

      if (!isServiceReady) {
        console.log('AI service not ready, returning fallback response');
        return AiConversationsApi.getFallbackResponse(context.selectedDocuments?.length > 0);
      }

      const {
        conversationId,
        selectedDocuments = [],
        quality_level = 'balanced',
        endeavorId = null
      } = context;

      const documentIds = selectedDocuments
        .map(docId => parseInt(docId))
        .filter(id => !isNaN(id));

      console.log('Calling AI with:', {
        message,
        conversationId,
        documentIds,
        endeavorId,
        quality_level
      });
      
      // Prefer endeavor_id when provided; otherwise, pass document_ids
      const aiResponse = await AiConversationsApi.promptWithConversationContext(message, {
        ...(conversationId && { conversation_id: conversationId }),
        ...(endeavorId ? { endeavor_id: endeavorId } : (documentIds.length > 0 ? { document_ids: documentIds } : {})),
        quality_level
      });

      if (aiResponse.ok) {
        const aiData = await aiResponse.json();
        console.log('AI response data:', aiData);

        const parsedResponse = AiConversationsApi.parseAIResponse(aiData);
        console.log('Parsed AI response:', parsedResponse);
        
        // ✅ FIXED: Store conversation messages with quality level
        if (conversationId && parsedResponse) {
          await storeConversationMessages(conversationId, message, parsedResponse, quality_level);
        }

        return parsedResponse;
      } else {
        console.error('AI service error:', aiResponse.status);
        return AiConversationsApi.getFallbackResponse(documentIds.length > 0);
      }
    } catch (error) {
      console.error('Error processing user message:', error);
      return AiConversationsApi.getFallbackResponse(context.selectedDocuments?.length > 0);
    }
  }, [isServiceReady, storeConversationMessages]);

  // Streamed message processing (SSE)
  const processUserMessageStream = useCallback(async (message, context = {}, handlers = {}) => {
    try {
      if (!isServiceReady) {
        handlers.onError?.("AI service unavailable");
        return { cancel: () => {} };
      }
      const {
        conversationId,
        selectedDocuments = [],
        quality_level = 'balanced',
        endeavorId = null,
        signal
      } = context;
      const documentIds = selectedDocuments.map(id => parseInt(id)).filter(n => !isNaN(n));
      return AiConversationsStream.promptWithContextStream(message, {
        ...(conversationId && { conversation_id: conversationId }),
        ...(endeavorId ? { endeavor_id: endeavorId } : (documentIds.length > 0 ? { document_ids: documentIds } : {})),
        quality_level,
        signal
      }, handlers);
    } catch (e) {
      handlers.onError?.(e?.message || 'Stream failed');
      return { cancel: () => {} };
    }
  }, [isServiceReady]);

  // ===================================
  // Utility Functions
  // ===================================

  const generateChatTitle = useCallback((messages) => {
    return AiConversationsApi.generateChatTitle(messages);
  }, []);

  const createWelcomeMessage = useCallback(() => {
    return AiConversationsApi.getWelcomeMessage();
  }, []);

  const createTypingMessage = useCallback(() => {
    return AiConversationsApi.getTypingMessage();
  }, []);

  // ✅ NEW: Get quality level options
  const getQualityLevelOptions = useCallback(() => {
    return AiConversationsApi.getQualityLevelOptions();
  }, []);

  return {
    // Service status
    isAiServiceAvailable: isServiceReady,
    
    // Conversation management
    loadExistingConversations,
    invalidateConversationCache,
    updateConversationTitle,
    patchConversation,
    deleteConversation,
    loadConversationMessages,
    loadMoreMessages,
    loadConversationDetails,

    // Message processing
    processUserMessage,
    processUserMessageStream,

    // Utility functions
    generateChatTitle,
    createWelcomeMessage,
    createTypingMessage,
    getQualityLevelOptions
  };
};
